import fscreen from 'fscreen';
import config, { EnterPriseType } from '@src/js/service/enterprise/temp-config';
import { IFullscreenHandler } from '@utils/fullscreen/fullscreen-handler.interface';
import { EventEmitter as EventBus } from '@src/slide/infrastructure/module/independent/event-emitter';
import type { IEventEmitter } from '@src/slide/infrastructure/module/independent/event-emitter/event-emitter.interface';
import tdocsBridge from '@src/slide/infrastructure/module/independent/tdocsBridge/tdocsBridge';
import ua from '@tencent/docs-user-agent';
import i18n from '@i18next/i18n';
import Snackbar from '@tencent/dui/lib/components/Snackbar';
import { enabledFullscreen } from '@src/utils/fullscreen';

export enum FullscreenEventsKeys {
    FULLSCREEN_STATUS_CHANGE = 'fullscreenStatusChange',
}

export interface FullscreenEventsMap {
    [FullscreenEventsKeys.FULLSCREEN_STATUS_CHANGE]: void;
}

export default class FullscreenHandler implements IFullscreenHandler {
    private static instance;

    public static getInstance(): FullscreenHandler {
        if (!FullscreenHandler.instance) {
            FullscreenHandler.instance = new FullscreenHandler();
        }
        return FullscreenHandler.instance;
    }

    public event: IEventEmitter<FullscreenEventsMap>;
    private isWebFullscreen: boolean;
    private isNativeFullscreen: boolean;

    public constructor() {
        this.event = new EventBus();

        this.onNativeFullscreen();
        enabledFullscreen && fscreen.addEventListener('fullscreenchange', this.onFullscreenChange);
    }

    public get fullscreenEnabled(): boolean {
        return enabledFullscreen && fscreen.fullscreenEnabled;
    }

    public get isFullscreen(): boolean {
        window.log.info(`[fullscreen] isFullScreen ${this.isWebFullscreen} ${this.isNativeFullscreen}`);
        // @ts-ignore
        const isFull = window.fullScreen || document.webkitIsFullScreen || document.msFullscreenEnabled;
        return isFull || this.isNativeFullscreen;
    }

    public get fullscreenElement(): Element {
        return enabledFullscreen && fscreen.fullscreenElement;
    }

    public requestFullscreen = (element: HTMLElement) => {
        if (config.supportEnterprise(EnterPriseType.APP) && tdocsBridge.tdocs?.requestFullscreen) {
            window.log.info('[fullscreen] requestFullscreen');
            return tdocsBridge.tdocs.requestFullscreen();
        }
        // mac 微信小程序不支持全屏
        if (ua.isWxMiniProgram && ua.isMac) {
            Snackbar.show({
                type: 'info',
                duration: 3000,
                message: i18n.t('小程序暂不支持全屏'),
            });
            return;
        }

        this.fullscreenEnabled && fscreen.requestFullscreen(element);
    };

    public exitFullscreen = () => {
        if (config.supportEnterprise(EnterPriseType.APP) && tdocsBridge.tdocs?.exitFullscreen) {
            window.log.info('[fullscreen] exitFullscreen');
            return tdocsBridge.tdocs.exitFullscreen();
        }

        this.fullscreenEnabled && fscreen.exitFullscreen();
    };

    private onNativeFullscreen = () => {
        if (config.supportEnterprise(EnterPriseType.APP)) {
            tdocsBridge.tdocs?.onFullscreenChange?.((result) => {
                window.log.info(`[fullscreen] nativeFullscreenChange ${JSON.stringify(result)}`);
                this.isNativeFullscreen = result.data;
                this.event.emit(FullscreenEventsKeys.FULLSCREEN_STATUS_CHANGE);
            });
        }
    };

    private onFullscreenChange = () => {
        const fullscreenEle = (enabledFullscreen && fscreen.fullscreenElement) || null;
        this.isWebFullscreen = !!(fullscreenEle);
    };
}
